import axios, { AxiosResponse } from 'axios';
import { BackendResponse, IAffiliate, IAppConfig, MangoPayAccount, MangoTransaction, SPV } from '../models';
import { REACT_APP_API_PLATFORM_URL } from '../util/config';

const api = axios.create({
  baseURL: `${REACT_APP_API_PLATFORM_URL}/api`,
  withCredentials: true,
});

export const getSPVs = () => {
  return api.get<BackendResponse<SPV[]>>('/v1/spv/partner');
};

/**
 * Get Account informaiton from MangoPay
 * @param mangopayId this is a unique number in mangopay, user identifier.
 * @param walletId wallet id of a user a number
 */

export const getMangoPayAccountInformation = (mangopayId: string, walletId: string) => {
  return api.get<BackendResponse<MangoPayAccount>>(`/v1/launchpad/issuer/account/${mangopayId}/${walletId}`);
};

/** Sends a message (in-app and email) to all investors of an asset. */
export const postInvestedUsersNotification = (assetId: string, body: { title: string; text: string }) => {
  return api.post(`/v1/notification/invested-users/${assetId}`, body);
};

/**
 * Get Mango Pay Transactions
 * @param mangopayId this is a unique number in mangopay, user identifier.
 * @param walletId wallet id of a user a number
 * @param page Pagination in Mangopay starts at 1 and not 0. Default is 1.
 * @param pageSize Default is 10. Max is 100.
 * @param from Standard Javascript Date ISO string
 * @param to Standard Javascript Date ISO string
 * @param mangopayTransactionStatus SUCCEEDED, FAILED
 * @param abortSignal Signal to abort request
 */
export const getMangoPayTransactions = async (
  mangopayId: string,
  walletId: string,
  from?: Date,
  to?: Date,
  page?: string,
  pageSize?: string,
  mangopayTransactionStatus?: string,
  abortSignal?: AbortSignal,
): Promise<BackendResponse<MangoTransaction[]>> => {
  if (!from) {
    from = new Date();
    from.setFullYear(from.getFullYear() - 1);
  }
  if (!to) {
    to = new Date();
  }
  if (!page) {
    page = '1';
  }
  if (!pageSize) {
    pageSize = '10';
  }
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('pageSize', pageSize);
  params.append('from', from.toISOString());
  params.append('to', to.toISOString());
  if (mangopayTransactionStatus) {
    params.append('mangopayTransactionStatus', mangopayTransactionStatus);
  }
  const { data } = await api.get<BackendResponse<MangoTransaction[]>>(
    `/v1/launchpad/issuer/transactions/${mangopayId}/${walletId}?${params.toString()}`,
    {
      signal: abortSignal,
    },
  );
  return data;
};

export async function downloadTransactionsCSV({
  from,
  to,
  status,
}: {
  from?: Date;
  to?: Date;
  status?: 'reserved' | 'success';
}): Promise<BackendResponse<MangoTransaction[]>> {
  if (!from) {
    from = new Date();
    from.setFullYear(from.getFullYear() - 1);
  }
  if (!to) {
    to = new Date();
  }

  const params = new URLSearchParams();
  if (status) {
    params.append('status', status);
  }
  params.append('from', from.toISOString());
  params.append('to', to.toISOString());
  const { data } = await api.get<BackendResponse<any>>(`/v1/transactions/bankWire/csv?${params.toString()}`);
  return data;
}

export const getAfACSV = (userId: string, assetId: string, year: string): Promise<AxiosResponse> => {
  return api.get(`/v1/launchpad/${userId}/${assetId}/afaCsv?year=${year}`, { responseType: 'text' });
};

export async function getAffiliates(partnerId: string) {
  return api.get<BackendResponse<IAffiliate[]>>(`/v1/affiliate/${partnerId}`);
}

export async function postAffiliate(partnerId: string, affiliate: IAffiliate) {
  return api.post<BackendResponse<IAffiliate>>(`/v1/affiliate/${partnerId}`, affiliate);
}

export async function getAppConfig(partnerId: string) {
  return api.get<BackendResponse<IAppConfig>>(`/v1/appconfig/${partnerId}`);
}

export type Transaction = {
  amount: number;
  id: string;
  userId: string;
  createdAt: string;
  assetId: string;
  assetTitle: string;
  /**
   * Full name of the user
   */
  name: string;
  email: string;
  status: string;
  paymentTransactionId: string;
  paymentTransactionStatus: string;
  notaryDocumentURL?: string;
};

export async function getTransactions(filters: {
  status: 'reserved' | 'success';
  page?: number;
  rowsPerPage?: number;
  sort?: 'newest' | 'oldest';
  userId?: string;
  dateMin?: string;
  dateMax?: string;
}) {
  return api.get<BackendResponse<{ data: Transaction[]; count: number }>>('/v1/transactions/bankWire', {
    params: {
      status: filters.status,
      page: filters.page,
      rowsPerPage: filters.rowsPerPage,
      sort: filters.sort,
      userId: filters.userId,
      dateMin: filters.dateMin,
      dateMax: filters.dateMax,
    },
  });
}

export async function changeChargeTransactionStatus(chargeTransactionId: string) {
  return api.put('/v1/transactions/bankWire/' + chargeTransactionId, {
    status: 'success',
  });
}

export async function addNotary(transactionId: string, url: string) {
  return api.post('/v1/transactions/notary/' + transactionId, {
    url,
  });
}
